import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import RecommendationService from '../services/recommendationService';
import { t } from '../utils/localization/localization';
import { logger } from '../utils/logger';

/**
 * Custom hook for managing training recommendations
 * @param {string} userId - Target user ID
 * @param {boolean} shouldLoadData - Whether to load data on mount
 * @returns {Object} Recommendation state and operations
 */
export const useRecommendations = (userId, shouldLoadData = true) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const [viewState, setViewState] = useState('initial'); // 'initial' or 'results'

  // Generate new recommendations
  const generateRecommendations = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const targetUserId = userId || currentUser?.id;
    logger.info('[useRecommendations]', 'Generating training recommendations for user:', targetUserId);
    logger.info('RESULT_PAGE', 'Generating training recommendations', { targetUserId });

    try {
      // Get recommendations from the analyzer service
      const newRecommendations = await RecommendationService.generateRecommendations(targetUserId);
      if (newRecommendations) {
        // Save recommendations to disk
        await RecommendationService.saveTrainingRecommendations(targetUserId, newRecommendations);

        // Update state with new recommendations
        setRecommendations(newRecommendations);
        setViewState('results');
        setSuccess(t('result.generatedSuccessfully'));

        logger.info('[useRecommendations]', 'Successfully generated recommendations');
        logger.info('RESULT_PAGE', 'Successfully generated training recommendations', {
          targetUserId,
          hasRecommendations: true
        });
      } else {
        throw new Error('Failed to generate recommendations');
      }
    } catch (error) {
      logger.error('[useRecommendations]', 'Error generating recommendations:', error);

      // Clear success state when error occurs to prevent simultaneous success+error display
      setSuccess('');

      const errorMessage = error.message || t('result.generationFailed');
      setError(errorMessage);

      logger.error('RESULT_PAGE', 'Error generating training recommendations', {
        targetUserId,
        error: error.message,
        displayedError: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  // Load existing recommendations
  const loadRecommendations = useCallback(async () => {
    setLoading(true);
    setError('');

    const targetUserId = userId || currentUser?.id;
    logger.info('[useRecommendations]', 'Loading recommendations for user:', targetUserId);
    logger.info('RESULT_PAGE', 'Loading training recommendations', { targetUserId });

    try {
      // Try to load existing recommendations
      const savedRecommendations = await RecommendationService.loadTrainingRecommendations(targetUserId);

      if (savedRecommendations) {
        logger.info('[useRecommendations]', 'Found saved recommendations');
        setRecommendations(savedRecommendations);
        setViewState('results');
        logger.info('RESULT_PAGE', 'Loaded existing recommendations', {
          targetUserId,
          hasRecommendations: true
        });
      } else {
        logger.info('[useRecommendations]', 'No saved recommendations found');
        setViewState('initial');
        logger.info('RESULT_PAGE', 'No existing recommendations found', {
          targetUserId
        });
      }
    } catch (error) {
      logger.error('[useRecommendations]', 'Error loading recommendations:', error);

      // Clear success state when error occurs to prevent simultaneous success+error display
      setSuccess('');

      setError(t('result.generationFailed'));
      setViewState('initial');
      logger.error('RESULT_PAGE', 'Error loading recommendations', {
        targetUserId,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  }, [userId, currentUser?.id]);

  // Auto-load recommendations on mount if enabled
  useEffect(() => {
    if (shouldLoadData && (userId || currentUser?.id)) {
      loadRecommendations();
    }
  }, [userId, shouldLoadData, loadRecommendations, currentUser?.id]);

  return {
    loading,
    error,
    setError,
    success,
    setSuccess,
    recommendations,
    viewState,
    setViewState,
    generateRecommendations,
    loadRecommendations
  };
};
